import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CalendarCheck } from 'lucide-react';
import { getMyReservations, cancelMyReservation } from 'api/requests/reservations';
import Loader from 'components/_UI/Loader';
import ToastContainer from 'components/_UI/ToastsContainer';
import ReservationStatusBadge from 'components/commons/ReservationStatusBadge';
import ConfirmDialog from 'modules/restaurant/menu/components/ConfirmDialog';
import useToast from 'hooks/useToast';
import colors from 'theme/colors';

const ACTIVE_STATUSES = ['pending', 'confirmed'];

// `starts_at` is a UTC instant computed by the backend from the establishment's timezone, so
// comparing it with the viewer's clock is correct anywhere. `can_cancel` is the backend's call.
const isUpcoming = (r) => ACTIVE_STATUSES.includes(r.status) && Date.parse(r.starts_at) > Date.now();

const startsAt = (r) => Date.parse(r.starts_at) || 0;

const formatDate = (dateStr) => {
    const [year, month, day] = (dateStr || '').split('-');
    return year && month && day ? `${day}/${month}/${year}` : dateStr;
};

const establishmentName = (r) => r.establishment?.name || 'Reserva de mesa';

export default function MyReservations() {
    const { toasts, toast } = useToast();
    const [reservations, setReservations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [cancelDialog, setCancelDialog] = useState({ open: false, reservation: null });
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                setReservations(await getMyReservations());
            } catch (error) {
                console.error('Error fetching my reservations', error);
                toast.error('Erro ao carregar suas reservas.');
            } finally {
                setLoading(false);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const { upcoming, history } = useMemo(() => ({
        upcoming: reservations
            .filter(isUpcoming)
            .sort((a, b) => startsAt(a) - startsAt(b)),
        history: reservations
            .filter(r => !isUpcoming(r))
            .sort((a, b) => startsAt(b) - startsAt(a)),
    }), [reservations]);

    const openCancelDialog = (reservation) => {
        setCancelReason('');
        setCancelDialog({ open: true, reservation });
    };

    const closeCancelDialog = () => {
        if (cancelling) return;
        setCancelDialog({ open: false, reservation: null });
        setCancelReason('');
    };

    const handleCancel = async () => {
        const { reservation } = cancelDialog;
        try {
            setCancelling(true);
            const updated = await cancelMyReservation(reservation.id, cancelReason.trim() || null);
            // Merge so fields the cancel response may omit (e.g. establishment) are kept.
            setReservations(prev => prev.map(r => r.id === reservation.id ? { ...r, ...updated, status: 'cancelled', can_cancel: false } : r));
            toast.success('Reserva cancelada.');
            setCancelDialog({ open: false, reservation: null });
            setCancelReason('');
        } catch (error) {
            console.error('Error cancelling reservation', error);
            toast.error(error?.response?.data?.message || 'Erro ao cancelar a reserva.');
        } finally {
            setCancelling(false);
        }
    };

    const renderCard = (reservation, isUpcomingCard) => (
        <Card key={reservation.id}>
            <CardTop>
                <CardTitle>{establishmentName(reservation)}</CardTitle>
                <ReservationStatusBadge status={reservation.status} />
            </CardTop>
            <Details>
                <span>{formatDate(reservation.reservation_date)}</span>
                <Dot />
                <span>{(reservation.reservation_time || '').slice(0, 5)}</span>
                <Dot />
                <span>{reservation.party_size} {reservation.party_size === 1 ? 'pessoa' : 'pessoas'}</span>
            </Details>
            {reservation.note && <Note>{reservation.note}</Note>}
            {reservation.status === 'cancelled' && reservation.cancel_reason && (
                <Note>Motivo do cancelamento: {reservation.cancel_reason}</Note>
            )}
            {isUpcomingCard && reservation.can_cancel && (
                <CancelButton type='button' onClick={() => openCancelDialog(reservation)}>
                    Cancelar reserva
                </CancelButton>
            )}
            {isUpcomingCard && !reservation.can_cancel && reservation.status === 'confirmed' && (
                <Note>Para cancelar uma reserva confirmada, entre em contato com o estabelecimento.</Note>
            )}
        </Card>
    );

    if (loading) return <Loader />;

    return (
        <>
            <ToastContainer toasts={toasts} />

            {upcoming.length === 0 && history.length === 0 ? (
                <EmptyState>
                    <EmptyIconCircle>
                        <CalendarCheck size={32} color={colors.occasionalPurple} />
                    </EmptyIconCircle>
                    <EmptyTitle>Você ainda não fez nenhuma reserva</EmptyTitle>
                    <EmptySubtitle>
                        Quando você reservar uma mesa em um estabelecimento, ela aparecerá aqui.
                    </EmptySubtitle>
                </EmptyState>
            ) : (
                <>
                    {upcoming.length > 0 && (
                        <Section>
                            <SectionTitle>Próximas</SectionTitle>
                            <CardList>{upcoming.map(r => renderCard(r, true))}</CardList>
                        </Section>
                    )}
                    {history.length > 0 && (
                        <Section>
                            <SectionTitle>Histórico</SectionTitle>
                            <CardList>{history.map(r => renderCard(r, false))}</CardList>
                        </Section>
                    )}
                </>
            )}

            <ConfirmDialog
                isOpen={cancelDialog.open}
                title='Cancelar reserva'
                message={`Tem certeza que deseja cancelar a reserva em "${cancelDialog.reservation ? establishmentName(cancelDialog.reservation) : ''}"?`}
                confirmLabel='Cancelar reserva'
                cancelLabel='Voltar'
                danger
                loading={cancelling}
                onConfirm={handleCancel}
                onCancel={closeCancelDialog}
            >
                <ReasonLabel htmlFor='my-cancel-reason'>Motivo (opcional)</ReasonLabel>
                <ReasonInput
                    id='my-cancel-reason'
                    value={cancelReason}
                    onChange={e => setCancelReason(e.target.value)}
                    placeholder='Ex: Mudança de planos'
                    rows={3}
                />
            </ConfirmDialog>
        </>
    );
}

const Section = styled.section`
    margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
    margin: 0 0 10px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const CardList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Card = styled.div`
    background: #fff;
    border-radius: 14px;
    padding: 16px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const CardTop = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
`;

const CardTitle = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Details = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: ${colors.nightDark};
`;

const Dot = styled.span`
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: ${colors.nightLight};
`;

const Note = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
    color: ${colors.night};
`;

const CancelButton = styled.button`
    align-self: flex-start;
    margin-top: 4px;
    padding: 8px 14px;
    border-radius: 999px;
    border: 1.5px solid ${colors.danger};
    background: #fff;
    color: ${colors.danger};
    font-size: 13px;
    font-weight: 600;
    font-family: inherit;
    cursor: pointer;

    &:hover { background: #fee2e2; }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
`;

const EmptyIconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #F3EAFF;
    margin-bottom: 20px;
`;

const EmptyTitle = styled.p`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 6px 0;
`;

const EmptySubtitle = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0;
    max-width: 360px;
`;

const ReasonLabel = styled.label`
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
`;

const ReasonInput = styled.textarea`
    width: 100%;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    padding: 10px 12px;
    font-size: 13px;
    font-family: inherit;
    resize: vertical;
    color: ${colors.nightDark};
    &:focus { outline: none; border-color: ${colors.occasionalPurple}; }
`;
